import { PageHero } from '@components/common';
import { Building2, ArrowRight, Scale } from 'lucide-react';
import { CALENDLY_URL } from '@constants';

const idealFor = [
  'Private equity firms evaluating new investments',
  'Strategic buyers acquiring technology companies',
  'Investors conducting Series B+ due diligence',
  'Companies evaluating merger partners',
  'Boards reviewing acquisition security risks',
  'Integration teams planning post-deal security',
];

const deliverables = [
  {
    title: 'Security Posture Assessment',
    description: 'Comprehensive evaluation of target company security controls and maturity',
  },
  {
    title: 'Risk and Liability Report',
    description: 'Identification of material cyber risks that could impact valuation or deal terms',
  },
  {
    title: 'Integration Risk Analysis',
    description: 'Assessment of security integration challenges and compatibility issues',
  },
  {
    title: 'Remediation Cost Estimate',
    description: 'Budget forecasts for addressing identified security gaps post-acquisition',
  },
  {
    title: 'Investment Committee Brief',
    description: 'Executive summary with risk ratings and investment decision support',
  },
];

const process = [
  {
    step: '01',
    title: 'Information Gathering',
    description: 'Review of security documentation, policies, and available audit reports',
    duration: '2-3 days',
  },
  {
    step: '02',
    title: 'Management Interviews',
    description: 'Structured discussions with CISO, CTO, and relevant technical leadership',
    duration: '1-2 days',
  },
  {
    step: '03',
    title: 'Technical Validation',
    description: 'Spot checks and validation of key security claims and control effectiveness',
    duration: '2-3 days',
  },
  {
    step: '04',
    title: 'Reporting',
    description: 'Draft findings, incorporate feedback, and deliver final assessment',
    duration: '2-3 days',
  },
];

export default function MaCyberDueDiligencePage() {
  const packageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'M&A Cyber Due Diligence',
    description:
      'Pre-acquisition security assessment to identify risks and liabilities. Protect your investment and plan integration before you sign.',
    url: 'https://thecisonetwork.com/packages/ma-cyber-due-diligence',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(packageSchema) }}
      />
      <PageHero
        title="M&A Cyber Due Diligence"
        subtitle="Pre-acquisition security assessment to identify risks and liabilities. Protect your investment and plan integration before you sign."
        eyebrow="Our Packages"
        seoTitle="M&A Cyber Due Diligence | The CISO Network"
        seoDescription="Pre-acquisition cybersecurity due diligence for investors and acquirers. Identify risks, liabilities, and integration challenges before you invest."
      />

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Know What You Are Buying
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Cybersecurity is now a standard component of M&A due diligence - and for good
                  reason. Undiscovered breaches, poor security practices, and compliance gaps can
                  destroy value and create unexpected liabilities.
                </p>
                <p>
                  Our M&A Cyber Due Diligence service provides investors and acquirers with a clear
                  understanding of a target company's security posture. We identify material risks,
                  quantify remediation costs, and highlight integration challenges that could impact
                  your investment thesis.
                </p>
                <p>
                  Whether you are evaluating a single investment or managing a portfolio, we give
                  you the security insights you need to make informed decisions and negotiate from a
                  position of knowledge.
                </p>
              </div>
            </div>

            <div className="bg-background rounded-2xl p-8 border border-border">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">At a Glance</h3>
              </div>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium text-foreground">1-2 weeks</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Target</span>
                  <span className="font-medium text-foreground">Any Acquisition</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Deliverables</span>
                  <span className="font-medium text-foreground">Risk Report + Brief</span>
                </div>
              </div>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-hero w-full justify-center"
              >
                Discuss This Package <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Ideal For
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {idealFor.map((item, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-teal/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-5 w-5 text-teal" />
                </div>
                <p className="text-muted-foreground text-sm pt-2">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              What You Receive
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Investment-grade analysis that supports your decision-making and protects your
              interests.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {deliverables.map((item, index) => (
              <div
                key={index}
                className="bg-background rounded-2xl p-8 border border-border hover:border-teal/30 transition-colors"
              >
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center mb-6">
                  <Scale className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="font-heading font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              How It Works
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              A rigorous, efficient process designed for the time pressures of M&A transactions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-5xl font-heading font-bold text-teal/20 mb-4">{step.step}</div>
                <h3 className="font-heading font-semibold text-foreground mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm mb-2">{step.description}</p>
                <span className="text-xs text-teal font-medium">{step.duration}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Protect Your Investment
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Cyber due diligence is no longer optional. Book a call to discuss your upcoming
            transaction and how we can help you understand the security risks before you sign.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
